import { Host, h } from "@stencil/core";
/**
 * The `ch-grid-column` component represents a grid column.
 */
export class ChGridColumn {
    constructor() {
        this.columnId = undefined;
        this.columnType = "plain";
        this.columnIconUrl = undefined;
        this.columnImage = undefined;
        this.columnImageSet = undefined;
        this.columnName = undefined;
        this.columnNameHidden = undefined;
        this.columnTooltip = undefined;
        this.columnNamePosition = "text";
        this.richRowDrag = undefined;
        this.richRowSelector = undefined;
        this.richRowSelectorMode = "select";
        this.richRowSelectorState = "";
        this.richRowActions = undefined;
        this.displayObserverClass = undefined;
        this.freeze = undefined;
        this.hidden = false;
        this.hideable = true;
        this.order = undefined;
        this.physicalOrder = undefined;
        this.size = undefined;
        this.resizable = true;
        this.resizing = undefined;
        this.sortable = true;
        this.settingable = true;
        this.sortDirection = undefined;
        this.showSettings = false;
    }
    dragging = false;
    dragMouseMoveFn = this.dragMouseMoveHandler.bind(this);
    dragMouseMoveStartPositionX;
    el;
    freezeHandler() {
        this.columnFreezeChanged.emit({
            columnId: this.columnId,
            freeze: this.freeze
        });
    }
    hiddenHandler() {
        this.columnHiddenChanged.emit({
            columnId: this.columnId,
            hidden: this.hidden
        });
    }
    orderHandler() {
        this.columnOrderChanged.emit({
            columnId: this.columnId,
            order: this.order
        });
    }
    sizeHandler() {
        this.columnSizeChanging.emit({
            columnId: this.columnId,
            size: this.size
        });
    }
    sortDirectionHandler() {
        if (this.sortDirection) {
            this.columnSortChanged.emit({
                columnId: this.columnId,
                sortDirection: this.sortDirection
            });
        }
    }
    /**
     * Event emitted when the `hidden` property is changed.
     */
    columnHiddenChanged;
    /**
     * Event emitted when the `size` property is currently being changed (i.e. when the user is dragging to resize the column).
     */
    columnSizeChanging;
    /**
     * Event emitted when the `size` property has been changed (i.e. when the user finishes dragging to resize the column).
     */
    columnSizeChanged;
    /**
     * Event emitted when the `order` property is changed.
     */
    columnOrderChanged;
    /**
     * Event emitted when the `sortDirection` property is changed.
     */
    columnSortChanged;
    /**
     * Event emitted when the `freeze` property is changed.
     */
    columnFreezeChanged;
    /**
     * Event emitted when the user is dragging the column header to move it.
     */
    columnDragStarted;
    /**
     * Event emitted when the user is dragging the column header to move it.
     */
    columnDragging;
    /**
     * Event emitted when the user stops dragging the column header to move it.
     */
    columnDragEnded;
    /**
     * Event emitted when the user clicks the row selector checkbox (only applicable for `richRowSelector="true"`.
     */
    columnSelectorClicked;
    componentDidLoad() {
        this.el.addEventListener("mousedown", this.mousedownHandler.bind(this));
    }
    clickHandler() {
        if (!this.dragging) {
            if (this.sortable) {
                this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
            }
        }
        else {
            this.dragging = false;
        }
    }
    columnResizeStartedHandler() {
        this.resizing = true;
    }
    columnResizeFinishedHandler() {
        this.resizing = false;
        this.columnSizeChanged.emit({
            columnId: this.columnId,
            size: this.size
        });
    }
    allowColumnReorder() {
        return this.el.closest("ch-grid").allowColumnReorder;
    }
    mousedownHandler(eventInfo) {
        eventInfo.preventDefault();
        eventInfo.stopPropagation();
        if (this.allowColumnReorder()) {
            this.dragMouseDownHandler(eventInfo);
            document.addEventListener("mousemove", this.dragMouseMoveFn, {
                passive: true
            });
            document.addEventListener("mouseup", this.dragMouseUpHandler.bind(this), {
                once: true
            });
        }
    }
    dragMouseDownHandler(eventInfo) {
        this.dragMouseMoveStartPositionX = eventInfo.pageX;
        this.columnDragStarted.emit({ columnId: this.columnId });
    }
    dragMouseMoveHandler(eventInfo) {
        if (this.dragging ||
            Math.abs(this.dragMouseMoveStartPositionX - eventInfo.pageX) > 5) {
            this.dragging = true;
            this.columnDragging.emit({
                columnId: this.columnId,
                positionX: eventInfo.pageX,
                direction: eventInfo.movementX > 0 ? "right" : "left"
            });
        }
    }
    dragMouseUpHandler() {
        document.removeEventListener("mousemove", this.dragMouseMoveFn);
        this.columnDragEnded.emit({ columnId: this.columnId });
    }
    settingsMouseDownHandler(eventInfo) {
        eventInfo.stopPropagation();
    }
    settingsClickHandler = (eventInfo) => {
        eventInfo.stopPropagation();
        this.showSettings = true;
    };
    selectorClickHandler = (eventInfo) => {
        const target = eventInfo.target;
        this.richRowSelectorState = target.checked ? "checked" : "";
        this.columnSelectorClicked.emit({
            checked: target.checked
        });
        eventInfo.stopPropagation();
    };
    render() {
        return (h(Host, null, h("ul", { class: "bar", part: "bar" }, this.renderSelector(), this.renderName(), this.renderSort(), this.renderSettings(), this.renderResize()), h("ch-grid-column-settings", { column: this.el, onMouseDown: this.settingsMouseDownHandler, show: this.showSettings, exportparts: "\n            mask:settings-mask,\n            window:settings-window,\n            header:settings-header,\n            caption:settings-caption,\n            close:settings-close,\n            main:settings-main,\n            footer:settings-footer\n          " }, h("slot", { name: "settings" }))));
    }
    renderSelector() {
        return (h("li", { class: "selector", part: "bar-selector", hidden: !(this.columnType === "rich" && this.richRowSelector) }, h("label", { part: "selector-label" }, h("input", { type: "checkbox", part: ["selector", this.richRowSelectorState]
                .filter(part => part !== "")
                .join(" "), onClick: this.selectorClickHandler, checked: this.richRowSelectorState === "checked", indeterminate: this.richRowSelectorState === "indeterminate" }))));
    }
    renderName() {
        return (h("li", { class: "name", part: "bar-name", title: this.columnTooltip ||
                (this.columnNamePosition === "title" ? this.columnName : null) }, this.columnIconUrl || this.columnImage || this.columnImageSet ? (h("img", { class: "name-icon", part: "bar-name-icon", src: this.columnImage || this.columnIconUrl, srcSet: this.columnImageSet })) : (h("div", { class: "name-icon", part: "bar-name-icon" })), h("span", { class: "name-text", part: "bar-name-text", hidden: this.columnNameHidden || this.columnNamePosition !== "text" }, this.columnName)));
    }
    renderSort() {
        return (h("li", { class: "sort", part: "bar-sort", hidden: !this.sortable }, h("div", { class: "sort-asc", part: "bar-sort-ascending" }), h("div", { class: "sort-desc", part: "bar-sort-descending" })));
    }
    renderSettings() {
        return (h("li", { class: "settings", part: "bar-settings", hidden: !this.settingable }, h("button", { class: "button", part: "bar-settings-button", onClick: this.settingsClickHandler })));
    }
    renderResize() {
        return (h("li", { class: "resize", part: "bar-resize", hidden: !this.resizable }, h("ch-grid-column-resize", { column: this.el, class: "resize-split", part: "bar-resize-split" })));
    }
    static get is() { return "ch-grid-column"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["ch-grid-column.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["ch-grid-column.css"]
        };
    }
    static get properties() {
        return {
            "columnId": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "A unique identifier for the column."
                },
                "attribute": "column-id",
                "reflect": false
            },
            "columnType": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"plain\" | \"rich\" | \"tree\"",
                    "resolved": "\"plain\" | \"rich\" | \"tree\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "One of \"plain\", \"rich\", or \"tree\", indicating the type of cell displayed in the column."
                },
                "attribute": "column-type",
                "reflect": false,
                "defaultValue": "\"plain\""
            },
            "columnIconUrl": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [{
                            "name": "deprecated",
                            "text": "Use \"columnImage\" or \"columnImageSet\" instead.\nA URL to an icon to display in the column header."
                        }],
                    "text": ""
                },
                "attribute": "column-icon-url",
                "reflect": false
            },
            "columnImage": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "A URL to an icon to display in the column header."
                },
                "attribute": "column-image",
                "reflect": false
            },
            "columnImageSet": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "A URL to an icon to display in the column header."
                },
                "attribute": "column-image-set",
                "reflect": false
            },
            "columnName": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The text to display in the column header and settings."
                },
                "attribute": "column-name",
                "reflect": false
            },
            "columnNameHidden": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Indicates whether the text in the column header is visible or not"
                },
                "attribute": "column-name-hidden",
                "reflect": false
            },
            "columnTooltip": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The text to display when the cursor is placed over the column header."
                },
                "attribute": "column-tooltip",
                "reflect": false
            },
            "columnNamePosition": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"text\" | \"title\"",
                    "resolved": "\"text\" | \"title\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [{
                            "name": "deprecated",
                            "text": "Use \"columnTooltip\" and \"columnNameHidden\" instead.\nOne of \"text\" or \"title\", indicating whether the `columnName` should be displayed as the column text or as tooltip of the column image."
                        }],
                    "text": ""
                },
                "attribute": "column-name-position",
                "reflect": false,
                "defaultValue": "\"text\""
            },
            "richRowDrag": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "A boolean value indicating whether the column cells are draggable to reorder the grid rows (only applicable for columnType=\"rich\")."
                },
                "attribute": "rich-row-drag",
                "reflect": false
            },
            "richRowSelector": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "A boolean indicating whether the column cells in the grid should have a checkbox selector (only applicable for columnType=\"rich\")."
                },
                "attribute": "rich-row-selector",
                "reflect": false
            },
            "richRowSelectorMode": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"select\" | \"mark\"",
                    "resolved": "\"mark\" | \"select\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "One of \"select\" or \"mark\", indicating the mode of rich row selector.\n\"select\" indicates that the row selector is bound to the row selection.\n\"mark\" allows to mark a row independently of the selection."
                },
                "attribute": "rich-row-selector-mode",
                "reflect": false,
                "defaultValue": "\"select\""
            },
            "richRowSelectorState": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "| \"\"\n    | \"checked\"\n    | \"indeterminate\"",
                    "resolved": "\"\" | \"checked\" | \"indeterminate\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Indicate the state of the rich row selector.\n\"\" indicates that all rows are unchecked.\n\"checked\" indicates that all rows are checked.\n\"indeterminate\" indicates that some rows are marked."
                },
                "attribute": "rich-row-selector-state",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "richRowActions": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "A boolean indicating whether the column cells in the grid should have a set of action buttons (only applicable for columnType=\"rich\")."
                },
                "attribute": "rich-row-actions",
                "reflect": false
            },
            "displayObserverClass": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "A CSS class name to apply to the display observer element used to detect changes in the column visibility."
                },
                "attribute": "display-observer-class",
                "reflect": false
            },
            "freeze": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "ChGridColumnFreeze",
                    "resolved": "\"end\" | \"start\"",
                    "references": {
                        "ChGridColumnFreeze": {
                            "location": "import",
                            "path": "./ch-grid-column-types",
                            "id": "src/components/grid/grid-column/ch-grid-column-types.ts::ChGridColumnFreeze"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "One of \"left\" or \"right\", indicating whether the column should be \"frozen\" (i.e. remain visible when the user scrolls horizontally)."
                },
                "attribute": "freeze",
                "reflect": false
            },
            "hidden": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "A boolean indicating whether the column should be hidden.\nThe user can display it from the grid settings."
                },
                "attribute": "hidden",
                "reflect": true,
                "defaultValue": "false"
            },
            "hideable": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "A boolean indicating whether the column should be hideable (i.e. whether the user should be able to show/hide the column)."
                },
                "attribute": "hideable",
                "reflect": false,
                "defaultValue": "true"
            },
            "order": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "A number indicating the order in which the column should appear."
                },
                "attribute": "order",
                "reflect": true
            },
            "physicalOrder": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "A number indicating the physical order of the column (i.e. its position in the DOM)."
                },
                "attribute": "physical-order",
                "reflect": false
            },
            "size": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "A string indicating the width of the column.\nAny value supported by the \"grid-template-columns\" CSS property is valid."
                },
                "attribute": "size",
                "reflect": true
            },
            "resizable": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "A boolean indicating whether the column should be resizable (i.e. whether the user should be able to drag its width)."
                },
                "attribute": "resizable",
                "reflect": false,
                "defaultValue": "true"
            },
            "resizing": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "A boolean indicating whether the column is currently being resized."
                },
                "attribute": "resizing",
                "reflect": true
            },
            "sortable": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "A boolean indicating whether the column should be sortable (i.e. whether the user should be able to click the column header to sort the data)."
                },
                "attribute": "sortable",
                "reflect": false,
                "defaultValue": "true"
            },
            "settingable": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "A boolean indicating whether the user should be able to open a settings panel for the column."
                },
                "attribute": "settingable",
                "reflect": false,
                "defaultValue": "true"
            },
            "sortDirection": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "ChGridColumnSortDirection",
                    "resolved": "\"asc\" | \"desc\"",
                    "references": {
                        "ChGridColumnSortDirection": {
                            "location": "import",
                            "path": "./ch-grid-column-types",
                            "id": "src/components/grid/grid-column/ch-grid-column-types.ts::ChGridColumnSortDirection"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "One of \"asc\" or \"desc\", indicating the current sort direction."
                },
                "attribute": "sort-direction",
                "reflect": true
            },
            "showSettings": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "A boolean indicating whether the settings panel for the column should be visible."
                },
                "attribute": "show-settings",
                "reflect": true,
                "defaultValue": "false"
            }
        };
    }
    static get events() {
        return [{
                "method": "columnHiddenChanged",
                "name": "columnHiddenChanged",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Event emitted when the `hidden` property is changed."
                },
                "complexType": {
                    "original": "ChGridColumnHiddenChangedEvent",
                    "resolved": "ChGridColumnHiddenChangedEvent",
                    "references": {
                        "ChGridColumnHiddenChangedEvent": {
                            "location": "import",
                            "path": "./ch-grid-column-types",
                            "id": "src/components/grid/grid-column/ch-grid-column-types.ts::ChGridColumnHiddenChangedEvent"
                        }
                    }
                }
            }, {
                "method": "columnSizeChanging",
                "name": "columnSizeChanging",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Event emitted when the `size` property is currently being changed (i.e. when the user is dragging to resize the column)."
                },
                "complexType": {
                    "original": "ChGridColumnSizeChangedEvent",
                    "resolved": "ChGridColumnSizeChangedEvent",
                    "references": {
                        "ChGridColumnSizeChangedEvent": {
                            "location": "import",
                            "path": "./ch-grid-column-types",
                            "id": "src/components/grid/grid-column/ch-grid-column-types.ts::ChGridColumnSizeChangedEvent"
                        }
                    }
                }
            }, {
                "method": "columnSizeChanged",
                "name": "columnSizeChanged",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Event emitted when the `size` property has been changed (i.e. when the user finishes dragging to resize the column)."
                },
                "complexType": {
                    "original": "ChGridColumnSizeChangedEvent",
                    "resolved": "ChGridColumnSizeChangedEvent",
                    "references": {
                        "ChGridColumnSizeChangedEvent": {
                            "location": "import",
                            "path": "./ch-grid-column-types",
                            "id": "src/components/grid/grid-column/ch-grid-column-types.ts::ChGridColumnSizeChangedEvent"
                        }
                    }
                }
            }, {
                "method": "columnOrderChanged",
                "name": "columnOrderChanged",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Event emitted when the `order` property is changed."
                },
                "complexType": {
                    "original": "ChGridColumnOrderChangedEvent",
                    "resolved": "ChGridColumnOrderChangedEvent",
                    "references": {
                        "ChGridColumnOrderChangedEvent": {
                            "location": "import",
                            "path": "./ch-grid-column-types",
                            "id": "src/components/grid/grid-column/ch-grid-column-types.ts::ChGridColumnOrderChangedEvent"
                        }
                    }
                }
            }, {
                "method": "columnSortChanged",
                "name": "columnSortChanged",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Event emitted when the `sortDirection` property is changed."
                },
                "complexType": {
                    "original": "ChGridColumnSortChangedEvent",
                    "resolved": "ChGridColumnSortChangedEvent",
                    "references": {
                        "ChGridColumnSortChangedEvent": {
                            "location": "import",
                            "path": "./ch-grid-column-types",
                            "id": "src/components/grid/grid-column/ch-grid-column-types.ts::ChGridColumnSortChangedEvent"
                        }
                    }
                }
            }, {
                "method": "columnFreezeChanged",
                "name": "columnFreezeChanged",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Event emitted when the `freeze` property is changed."
                },
                "complexType": {
                    "original": "ChGridColumnFreezeChangedEvent",
                    "resolved": "ChGridColumnFreezeChangedEvent",
                    "references": {
                        "ChGridColumnFreezeChangedEvent": {
                            "location": "import",
                            "path": "./ch-grid-column-types",
                            "id": "src/components/grid/grid-column/ch-grid-column-types.ts::ChGridColumnFreezeChangedEvent"
                        }
                    }
                }
            }, {
                "method": "columnDragStarted",
                "name": "columnDragStarted",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Event emitted when the user is dragging the column header to move it."
                },
                "complexType": {
                    "original": "ChGridColumnDragEvent",
                    "resolved": "ChGridColumnDragEvent",
                    "references": {
                        "ChGridColumnDragEvent": {
                            "location": "import",
                            "path": "./ch-grid-column-types",
                            "id": "src/components/grid/grid-column/ch-grid-column-types.ts::ChGridColumnDragEvent"
                        }
                    }
                }
            }, {
                "method": "columnDragging",
                "name": "columnDragging",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Event emitted when the user is dragging the column header to move it."
                },
                "complexType": {
                    "original": "ChGridColumnDragEvent",
                    "resolved": "ChGridColumnDragEvent",
                    "references": {
                        "ChGridColumnDragEvent": {
                            "location": "import",
                            "path": "./ch-grid-column-types",
                            "id": "src/components/grid/grid-column/ch-grid-column-types.ts::ChGridColumnDragEvent"
                        }
                    }
                }
            }, {
                "method": "columnDragEnded",
                "name": "columnDragEnded",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Event emitted when the user stops dragging the column header to move it."
                },
                "complexType": {
                    "original": "ChGridColumnDragEvent",
                    "resolved": "ChGridColumnDragEvent",
                    "references": {
                        "ChGridColumnDragEvent": {
                            "location": "import",
                            "path": "./ch-grid-column-types",
                            "id": "src/components/grid/grid-column/ch-grid-column-types.ts::ChGridColumnDragEvent"
                        }
                    }
                }
            }, {
                "method": "columnSelectorClicked",
                "name": "columnSelectorClicked",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Event emitted when the user clicks the row selector checkbox (only applicable for `richRowSelector=\"true\"`."
                },
                "complexType": {
                    "original": "ChGridColumnSelectorClickedEvent",
                    "resolved": "ChGridColumnSelectorClickedEvent",
                    "references": {
                        "ChGridColumnSelectorClickedEvent": {
                            "location": "import",
                            "path": "./ch-grid-column-types",
                            "id": "src/components/grid/grid-column/ch-grid-column-types.ts::ChGridColumnSelectorClickedEvent"
                        }
                    }
                }
            }];
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "freeze",
                "methodName": "freezeHandler"
            }, {
                "propName": "hidden",
                "methodName": "hiddenHandler"
            }, {
                "propName": "order",
                "methodName": "orderHandler"
            }, {
                "propName": "size",
                "methodName": "sizeHandler"
            }, {
                "propName": "sortDirection",
                "methodName": "sortDirectionHandler"
            }];
    }
    static get listeners() {
        return [{
                "name": "click",
                "method": "clickHandler",
                "target": undefined,
                "capture": false,
                "passive": true
            }, {
                "name": "columnResizeStarted",
                "method": "columnResizeStartedHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "columnResizeFinished",
                "method": "columnResizeFinishedHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }];
    }
}
//# sourceMappingURL=ch-grid-column.js.map
